/**
 * API unificada para gestión de turnos/appointments
 * 
 * Este servicio reemplaza múltiples endpoints legacy con un único endpoint
 * /api/appointments que maneja todas las operaciones de turnos.
 * 
 * @module services/appointmentsApi
 */

import apiMonolito from './apiMonolito';
import store from '@/store';
import * as utils from '@/utils';
import dateFormat from 'dateformat';
import LZString from 'lz-string';

const CUSTOM_TIPO_VISITA_STORAGE_KEY = 'gnuhealth_custom_tipo_visita';

function getCustomTipoVisitaFromStorage() {
  if (typeof window === 'undefined' || !window.localStorage) return [];

  try {
    const raw = window.localStorage.getItem(CUSTOM_TIPO_VISITA_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((item, index) => {
        if (!item || !item.name) return null;
        return {
          id: item.id || `local-tipo-visita-${index + 1}`,
          name: item.name,
          descripcion: item.descripcion || '',
          categoria: 'tipo_visita',
          active: true,
          local: true
        };
      })
      .filter(Boolean);
  } catch (error) {
    return [];
  }
}

function saveCustomTipoVisitaInStorage(newItem) {
  if (typeof window === 'undefined' || !window.localStorage || !newItem || !newItem.name) return;

  const current = getCustomTipoVisitaFromStorage();
  const exists = current.some(
    item => (item.name || '').trim().toLowerCase() === newItem.name.trim().toLowerCase()
  );

  if (exists) return;

  const next = [
    ...current,
    {
      id: newItem.id || `local-tipo-visita-${Date.now()}`,
      name: newItem.name,
      descripcion: newItem.descripcion || '',
      categoria: 'tipo_visita',
      active: true,
      local: true
    }
  ];

  window.localStorage.setItem(CUSTOM_TIPO_VISITA_STORAGE_KEY, JSON.stringify(next));
}

// Helper debridement function
function safeDecompressAndParse(data) {
  if (typeof data === 'object' && data !== null) return data;
  try {
    if (!data) return [];
    const decoded = LZString.decompressFromBase64(data);
    return decoded ? JSON.parse(decoded) : [];
  } catch (e) {
    console.error("Error decompressing:", e);
    return [];
  }
}

// ============================================================================
// FUNCIONES HELPER
// ============================================================================

/**
 * Decodifica y normaliza la respuesta de turnos del servidor
 * @param {Array} data - Datos crudos del servidor
 * @returns {Object} { citas: Array, pacientes: Array }
 */
export function decodeAppointments(data) {
  if (!Array.isArray(data)) {
    return { citas: [], pacientes: [] };
  }

  const citas = [];
  const pacientes = [];

  // Iterate strictly to handle interleaved pairs [Appointment, PatientInfo]
  for (let i = 0; i < data.length; i++) {
    const item = data[i];

    if (item.appointment_date) {
      // It's an appointment
      let patientData = {};

      // Check if next item is the paired patient info (not an appointment)
      if (i + 1 < data.length) {
        const nextItem = data[i + 1];
        if (!nextItem.appointment_date) {
          patientData = nextItem;
          i++; // Consume the patient object
        }
      }

      // Process dates
      const fechaUTC = utils.convertUTCDateToLocalDate(item.appointment_date);
      const fechaEnd = utils.convertUTCDateToLocalDate(item.appointment_date_end);

      // Construct Patient Name safely
      let patientName = 'Sin nombre';
      if (patientData && (patientData.name || patientData.firstname)) {
        const pName = patientData.name || patientData.firstname || '';
        const pLast = patientData.lastname || patientData.surname || '';
        patientName = pLast ? `${pName} ${pLast}` : pName;
      }

      citas.push({
        ...item,
        start: dateFormat(fechaUTC, "yyyy-mm-dd HH:MM:ss"),
        end: dateFormat(fechaEnd, "yyyy-mm-dd HH:MM:ss"),
        title: item.name || '',
        // Enriched fields
        patient_name: patientName,
        party: patientData // Keep full reference
      });

      if (patientData.name || patientData.ref) {
        pacientes.push(patientData);
      }
    } else {
      // Orphaned patient object or other data
      if (item.name || item.nombre) pacientes.push(item);
    }
  }

  return { citas, pacientes };
}

/**
 * Filtra turnos por estado
 * @param {Array} citas - Lista de citas
 * @param {string|Array} estados - Estado(s) a filtrar ('free', 'confirmed', etc.)
 * @returns {Array} Citas filtradas
 */
export function filterByState(citas, estados) {
  if (!Array.isArray(citas)) return [];

  const estadosArray = Array.isArray(estados) ? estados : [estados];

  return citas.filter(function (cita) {
    return estadosArray.includes(cita.state);
  });
}

// ============================================================================
// API DE TURNOS UNIFICADA
// ============================================================================

/**
 * Obtiene turnos según filtros
 * 
 * @param {Object} params - Parámetros de consulta
 * @param {number} [params.profesionalId] - ID del profesional
 * @param {boolean} [params.all] - Si es true, obtiene todos los turnos (secretaria)
 * @param {string} [params.paciente] - Email del paciente
 * @param {string} [params.pacienteDni] - DNI del paciente
 * @param {boolean} [params.doctor] - Si es true, obtiene turnos del doctor logueado
 * @param {boolean} [params.historial] - Si es true, incluye historial
 * @param {string} [params.state] - Filtrar por estado ('free', 'confirmed', etc.)
 * @returns {Promise<Object>} { citas: Array, pacientes: Array }
 */
export async function getAppointments(params = {}) {
  const queryParams = new URLSearchParams();
  let baseUrl = '/appointmentsdr'; // Default a appointmentsdr (el único endpoint que existe)

  // Lógica para resolver ID de profesional si es médico
  let professionalId = params.profesionalId;

  if (params.doctor) {
    const userProfile = store.state.user_profile;

    // Si ya tenemos el ID profesional guardado, lo usamos
    if (userProfile && userProfile.professional_id) {
      professionalId = userProfile.professional_id;
      queryParams.set('profesional_id', professionalId);
    }
    // Si no, y tenemos DNI, lo buscamos
    else if (userProfile && userProfile.dni) {
      try {
        const profResponse = await apiMonolito.get(`/profesional/${userProfile.dni}`);

        let profData = null;
        if (Array.isArray(profResponse.data) && profResponse.data.length > 0) profData = profResponse.data[0];
        else if (profResponse.data && !Array.isArray(profResponse.data)) profData = profResponse.data;

        if (profData && profData.id) {
          // Guardar en store para no buscar de nuevo
          store.commit('set_user_profile', { ...userProfile, professional_id: profData.id });
          professionalId = profData.id;
          queryParams.set('profesional_id', profData.id);
        }
      } catch (e) {
        // Error buscando profesional
      }
    }
  }

  // Si hay profesionalId (por params o por resolución de doctor), usar endpoint con ID
  if (professionalId) {
    baseUrl = `/appointmentsdr/${professionalId}`;
  }

  if (params.profesionalId) queryParams.set('profesional_id', params.profesionalId);
  if (params.all) queryParams.set('all', 'true');
  if (params.paciente) queryParams.set('paciente', params.paciente);
  if (params.pacienteDni) queryParams.set('paciente_dni', params.pacienteDni);
  if (params.doctor) queryParams.set('doctor', 'true');
  if (params.historial) queryParams.set('historial', 'true');

  const queryString = queryParams.toString();
  // Si baseUrl ya tiene el path /appointmentsdr/ID, lo usamos. Si no, default.
  // Pero aquí baseUrl ya se definió arriba si es doctor.
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

  try {
    const response = await apiMonolito.get(url);
    const decompressedData = safeDecompressAndParse(response.data);
    const decoded = decodeAppointments(decompressedData);

    // Filtrar por estado si se especificó
    if (params.state) {
      decoded.citas = filterByState(decoded.citas, params.state);
    }

    return decoded;
  } catch (error) {
    return { citas: [], pacientes: [] };
  }
}

/**
 * Obtiene turnos disponibles (estado 'free') de un profesional
 * 
 * @param {number} profesionalId - ID del profesional
 * @returns {Promise<Array>} Lista de citas disponibles
 */
export async function getTurnosDisponibles(profesionalId) {
  const result = await getAppointments({
    profesionalId,
    state: ['free', 'user_cancelled'],
    all: true
  });
  return result.citas;
}

/**
 * Obtiene todos los turnos de un profesional (para secretaria)
 * 
 * @param {number} profesionalId - ID del profesional
 * @returns {Promise<Object>} { citas: Array, pacientes: Array }
 */
export async function getTodosTurnos(profesionalId) {
  // Usar endpoint appointmentsdr directamente
  try {
    const url = `/appointmentsdr/${profesionalId}?all=true`;
    const response = await apiMonolito.get(url);
    const decompressedData = safeDecompressAndParse(response.data);
    return decodeAppointments(decompressedData);
  } catch (error) {
    console.error('Error getTodosTurnos:', error);
    return { citas: [], pacientes: [] };
  }
}

/**
 * Obtiene turnos cancelados por usuario (para re-disponibilizar)
 * Fuerza el uso del endpoint unificado para asegurar filtrado correcto
 * 
 * @param {number} profesionalId - ID del profesional
 * @returns {Promise<Array>} Lista de citas canceladas
 */
export async function getTurnosCancelados(profesionalId) {
  try {
    const url = '/appointments'; // Endpoint unificado (baseURL ya contiene /api)

    const response = await apiMonolito.get(url, {
      params: {
        profesional_id: profesionalId,
        all: true
      }
    });

    const decoded = decodeAppointments(response.data);
    return filterByState(decoded.citas, 'user_cancelled');

  } catch (error) {
    return [];
  }
}

/**
 * Obtiene turnos del paciente por email
 * 
 * @param {string} email - Email del paciente
 * @returns {Promise<Array>} Lista de citas del paciente
 */
export async function getTurnosPaciente(email) {
  const result = await getAppointments({ paciente: email });
  return result.citas;
}

/**
 * Obtiene turnos del paciente por DNI (para secretaria)
 * 
 * @param {string} dni - DNI del paciente
 * @returns {Promise<Array>} Lista de citas del paciente
 */
export async function getTurnosPacientePorDni(dni) {
  const result = await getAppointments({ pacienteDni: dni });
  return result.citas;
}

/**
 * Obtiene turnos del doctor logueado
 * 
 * @param {boolean} historial - Si es true, incluye historial
 * @returns {Promise<Array>} Lista de citas del doctor
 */
export async function getTurnosDoctor(historial = false) {
  const result = await getAppointments({
    doctor: true,
    historial
  });
  return result.citas;
}

// ============================================================================
// RESERVAR Y ACTUALIZAR TURNOS
// ============================================================================

/**
 * Reserva un turno (paciente)
 * 
 * @param {number} appointmentId - ID del turno a reservar
 * @param {string} dni - DNI del paciente
 * @param {string} [nombre] - Nombre del paciente (opcional)
 * @returns {Promise<Object>} { success: boolean, status: number, data?: any, error?: string }
 */
export async function reservarTurno(appointmentId, dni, nombre) {
  try {
    const payload = {
      dni: dni
    };

    if (nombre) {
      payload.name = nombre;
    }

    // Endpoint legacy compatible: /appointments/{user_id}/{appointment_id}
    const response = await apiMonolito.patch(`/appointments/${dni}/${appointmentId}`, payload);

    return { success: true, status: 200, data: response.data };
  } catch (error) {
    const status = error.response ? error.response.status : 500;

    if (status === 423) {
      return { success: false, status: 423, error: 'Turno duplicado' };
    }

    return { success: false, status: status, error: error.message };
  }
}

/**
 * Reserva un turno (secretaria)
 * Usa el endpoint específico para secretarias
 * 
 * @param {number} appointmentId - ID del turno
 * @param {Object} data - Datos del paciente
 * @param {string} data.dni - DNI del paciente
 * @param {number} [data.paciente_id] - ID del paciente
 * @param {string} [data.name] - Nombre del paciente
 * @returns {Promise<Object>} { success: boolean, status: number }
 */
export async function reservarTurnoSecretaria(appointmentId, data) {
  try {
    // Endpoint específico para secretarias: /appointmentsscr/<user_id>/<id>
    // user_id es el DNI del paciente en este contexto
    const dni = data.dni || data.paciente_id;
    const payload = {
      eventID: appointmentId,
      dni,
      ...data
    };

    // Mapear tipoDeConsulta a tipo_visita si existe
    if (data.tipoDeConsulta) {
      payload.tipo_visita = data.tipoDeConsulta;
    }

    const response = await apiMonolito.patch(`/appointmentsscr/${dni}/${appointmentId}`, payload);

    return { success: true, status: 200, data: response.data };
  } catch (error) {
    const status = error.response ? error.response.status : 500;

    if (status === 423) {
      const backendMsg = error.response?.data?.error || error.response?.data?.message;
      return {
        success: false,
        status: 423,
        error: backendMsg || 'Turno bloqueado o duplicado (ya está asignado)'
      };
    }

    return { success: false, status: status, error: error.message };
  }
}

/**
 * Cambia el estado de un turno
 * Backend: PATCH /appointmentsstate/<user_id>/<appointment_id>
 * 
 * @param {number} appointmentId - ID del turno
 * @param {string} estado - Nuevo estado ('confirmed', 'checked_in', 'done', 'user_cancelled', 'center_cancelled')
 * @param {string} [userId] - ID del usuario (DNI o ID de profesional), si no se provee se usa el ID del turno
 * @returns {Promise<Object>} { success: boolean, status: number }
 */
export async function cambiarEstadoTurno(appointmentId, estado, userId = null) {
  try {
    // Si no se provee userId, usar appointmentId como fallback (algunas versiones del backend lo aceptan)
    const user = userId || appointmentId;

    const response = await apiMonolito.patch(`/appointmentsstate/${user}/${appointmentId}`, {
      eventID: appointmentId,
      estado: estado
    });

    return { success: true, status: 200, data: response.data };
  } catch (error) {
    const status = error.response ? error.response.status : 500;
    return { success: false, status: status, error: error.message };
  }
}

/**
 * Cancela un turno (alias de cambiarEstadoTurno con estado 'user_cancelled')
 * 
 * @param {number} appointmentId - ID del turno
 * @returns {Promise<Object>} { success: boolean, status: number }
 */
export async function cancelarTurno(appointmentId) {
  return await cambiarEstadoTurno(appointmentId, 'user_cancelled');
}

// ============================================================================
// CREAR SOBRETURNOS
// ============================================================================

/**
 * Crea un sobreturno
 * 
 * @param {Object} data - Datos del sobreturno
 * @param {number} data.profesional_id - ID del profesional
 * @param {string} data.paciente_dni - DNI del paciente
 * @param {string} data.start - Fecha/hora inicio (ISO 8601)
 * @param {string} data.end - Fecha/hora fin (ISO 8601)
 * @param {string} [data.tipo_visita] - Tipo de visita
 * @returns {Promise<Object>} { success: boolean, status: number, data?: any }
 */
export async function crearSobreturno(data) {
  try {
    const { paciente_dni, profesional_id, start, tipo_visita, paciente_nombre, paciente_telefono } = data;

    // Usar el endpoint correcto del backend: PUT /new_appointments/{dni}/{profesional_id}
    const url = `/new_appointments/${paciente_dni}/${profesional_id}`;
    const payload = {
      date: start, // ISO 8601 date string
      tipo_visita: tipo_visita || 'Consulta de seguimiento',
      name: paciente_nombre,
      phone: paciente_telefono
    };

    const response = await apiMonolito.put(url, payload);

    return { success: true, status: 200, data: response.data };
  } catch (error) {
    const status = error.response ? error.response.status : 500;
    return { success: false, status: status, error: error.message };
  }
}

// ============================================================================
// PRÁCTICAS
// ============================================================================

/**
 * Obtiene el catálogo de prácticas/tipos de visita
 *
 * @param {string|null} [categoria] - Filtrar por categoría: 'tipo_visita', 'practica', 'laboratorio', 'imagen'
 * @returns {Promise<Array>} Lista de prácticas [{id, name, descripcion, categoria, active}]
 */
export async function getPracticas(categoria = null) {
  try {
    const params = {};
    if (categoria) params.categoria = categoria;
    const response = await apiMonolito.get('/practicas', { params });

    const backendData = Array.isArray(response.data) ? response.data : [];
    const customTipos = getCustomTipoVisitaFromStorage();

    if (categoria && categoria !== 'tipo_visita') {
      return backendData;
    }

    const existingNames = new Set(
      backendData
        .filter(item => item && item.categoria === 'tipo_visita' && item.name)
        .map(item => item.name.trim().toLowerCase())
    );

    const mergedCustomTipos = customTipos.filter(
      item => item.name && !existingNames.has(item.name.trim().toLowerCase())
    );

    if (categoria === 'tipo_visita') {
      return [...backendData, ...mergedCustomTipos];
    }

    return [...backendData, ...mergedCustomTipos];
  } catch (error) {
    console.error('Error getPracticas:', error);

    // Fallback local para no bloquear la UX si falla el backend.
    if (categoria === 'tipo_visita' || categoria === null) {
      return getCustomTipoVisitaFromStorage();
    }

    return [];
  }
}

/**
 * Crea una práctica/tipo de visita.
 * Si el backend no soporta alta de prácticas, persiste localmente como fallback.
 *
 * @param {Object} payload
 * @param {string} payload.name - Nombre de la práctica
 * @param {string} payload.categoria - Categoría ('tipo_visita', 'practica', etc.)
 * @param {string} [payload.descripcion] - Descripción opcional
 * @returns {Promise<Object>} { success: boolean, data?: Object, localFallback?: boolean, error?: string }
 */
export async function createPractica(payload) {
  const normalizedPayload = {
    name: (payload?.name || '').trim(),
    categoria: payload?.categoria || 'tipo_visita',
    descripcion: payload?.descripcion || '',
    active: payload?.active !== false
  };

  if (!normalizedPayload.name) {
    return { success: false, error: 'El nombre es obligatorio' };
  }

  try {
    const response = await apiMonolito.post('/practicas', normalizedPayload);
    const created = response.data || normalizedPayload;

    if (created.categoria === 'tipo_visita') {
      saveCustomTipoVisitaInStorage(created);
    }

    return { success: true, data: created, localFallback: false };
  } catch (error) {
    // Fallback local para mantener continuidad operativa en frontend.
    if (normalizedPayload.categoria === 'tipo_visita') {
      const localCreated = {
        id: `local-tipo-visita-${Date.now()}`,
        ...normalizedPayload,
        local: true
      };

      saveCustomTipoVisitaInStorage(localCreated);
      return { success: true, data: localCreated, localFallback: true };
    }

    const status = error.response ? error.response.status : 500;
    return { success: false, status, error: error.message };
  }
}

/**
 * Asigna prácticas a un turno (reemplaza las existentes)
 *
 * @param {number} appointmentId - ID del turno
 * @param {number[]} practicaIds - Array de IDs de prácticas a asignar
 * @returns {Promise<Object>} { success: boolean, data?: Array, error?: string }
 */
export async function setAppointmentPracticas(appointmentId, practicaIds) {
  try {
    const response = await apiMonolito.post(
      `/appointments/${appointmentId}/practicas`,
      { practicas: practicaIds }
    );
    return { success: true, data: response.data };
  } catch (error) {
    const status = error.response ? error.response.status : 500;
    return { success: false, status, error: error.message };
  }
}

// ============================================================================
// EXPORT DEFAULT (para compatibilidad)
// ============================================================================

export default {
  // Consultas
  getAppointments,
  getTurnosDisponibles,
  getTodosTurnos,
  getTurnosPaciente,
  getTurnosPacientePorDni,
  getTurnosDoctor,

  // Reservar/Actualizar
  reservarTurno,
  reservarTurnoSecretaria,
  cambiarEstadoTurno,
  cancelarTurno,

  // Crear
  crearSobreturno,

  // Prácticas
  getPracticas,
  createPractica,
  setAppointmentPracticas,

  // Helpers
  decodeAppointments,
  filterByState
};
